import React, { useState, useRef, useContext } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Menu,
  MenuItem,
  IconButton,
  Avatar,
  Box,
  Skeleton,
  useMediaQuery,
} from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import ListAltIcon from "@mui/icons-material/ListAlt";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import QueueIcon from "@mui/icons-material/Queue";
import SettingsIcon from "@mui/icons-material/Settings";
import LogoutIcon from "@mui/icons-material/Logout";
import { useTheme, keyframes } from "@mui/material/styles";
import supabase from "./supabaseClient";
import UserContext from "./contexts/userContext";

// Very slow shimmer animation - ambient, not distracting
const slowShimmer = keyframes`
  0% { background-position: 200% center; }
  100% { background-position: -200% center; }
`;

// Pulse animation for loading avatar
const pulse = keyframes`
  0%, 100% { opacity: 0.4; }
  50% { opacity: 0.8; }
`;

// Base menu items configuration with icons
const baseMenuItems = [
  { path: "/lists", label: "lists", icon: ListAltIcon },
  { path: "/books", label: "books", icon: MenuBookIcon },
  { path: "/meal-plan", label: "meal planner", icon: CalendarMonthIcon },
  { path: "/upload", label: "upload", icon: CloudUploadIcon },
  { path: "/settings", label: "settings", icon: SettingsIcon },
];

function Header({ toggleTheme, themeMode }) {
  const { user } = useContext(UserContext);
  const [menuOpen, setMenuOpen] = useState(false);
  const anchorRef = useRef(null);
  const theme = useTheme();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  
  // Accent colors for the new design
  const isDark = theme.palette.mode === "dark";
  const accentPrimary = isDark ? "#BB86FC" : "#6366f1";
  const accentSecondary = isDark ? "#03DAC6" : "#8b5cf6";
  
  // Only show shimmer on search homepage (no query params)
  const isSearchHomepage = location.pathname === "/" && !location.search;

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    window.location.reload();
  };

  const handleMenuOpen = () => {
    setMenuOpen(true);
  };

  const handleMenuClose = () => {
    setMenuOpen(false);
  };

  const avatarUrl = user?.user_metadata?.avatar_url;
  const isAdmin = user?.user_role === "admin";

  const menuItems = React.useMemo(() => {
    const items = [...baseMenuItems];
    if (isAdmin) {
      // Admin-only shortcuts
      items.push({ path: "/upload/admin", label: "admin upload", icon: CloudUploadIcon });
      items.push({ path: "/admin/queues", label: "queues", icon: QueueIcon });
    }
    return items;
  }, [isAdmin]);

  // Function to handle logo click and reload the page
  const handleLogoClick = () => {
    window.location.href = "/";
  };

  return (
    <AppBar
      position="static"
      color="default"
      elevation={0}
      sx={{
        // Refined glassmorphism
        backgroundColor: isDark 
          ? "rgba(18, 18, 18, 0.85)" 
          : "rgba(255, 255, 255, 0.85)",
        backdropFilter: "blur(16px)",
        WebkitBackdropFilter: "blur(16px)",
        color: theme.palette.text.primary,
        borderBottom: "none",
        position: "relative",
        // Gradient bottom border - shimmer only on search homepage
        "&::after": {
          content: '""',
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: "2px",
          background: `linear-gradient(90deg, 
            transparent 0%, 
            ${accentPrimary}40 15%, 
            ${accentPrimary} 35%,
            ${accentSecondary} 65%,
            ${accentSecondary}40 85%,
            transparent 100%)`,
          backgroundSize: "200% 100%",
          animation: isSearchHomepage ? `${slowShimmer} 25s linear infinite` : "none",
          transition: "opacity 0.5s ease",
        },
      }}
    >
      <Box
        sx={{
          mx: "auto",
          width: "100%",
        }}
      >
        <Toolbar
          sx={{
            padding: isMobile ? "4px 8px" : "6px 12px",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          {/* Logo */}
          <Typography
            variant="h4"
            sx={{
              fontFamily: "Candal",
              cursor: "pointer",
              fontSize: isMobile ? "30px" : "32px",
              lineHeight: 1,
              userSelect: "none",
              // Gradient text effect
              background: `linear-gradient(135deg, ${accentPrimary} 0%, ${accentSecondary} 100%)`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "scale(1.05)",
                filter: `drop-shadow(0 0 20px ${accentPrimary}40)`,
              },
              // Focus state for keyboard navigation
              "&:focus-visible": {
                outline: `2px solid ${accentPrimary}`,
                outlineOffset: "4px",
                borderRadius: "4px",
              },
            }}
            onClick={handleLogoClick}
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && handleLogoClick()}
          >
            {"{r}"}
          </Typography>

          {/* Right section: Theme + User */}
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
          >
            {/* Theme toggle with smooth transition */}
            <IconButton 
              onClick={toggleTheme} 
              sx={{ 
                width: 40,
                height: 40,
                borderRadius: "12px",
                color: "text.secondary",
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  backgroundColor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
                  color: "text.primary",
                  transform: "rotate(15deg)",
                },
                "&:focus-visible": {
                  outline: `2px solid ${accentPrimary}`,
                  outlineOffset: "2px",
                },
                // Smooth icon transition
                "& svg": {
                  transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                },
                "&:active svg": {
                  transform: "rotate(180deg)",
                },
              }}
            >
              {themeMode === "dark" ? <Brightness7Icon fontSize="small" /> : <Brightness4Icon fontSize="small" />}
            </IconButton>
            
            {user === undefined ? (
              // Better loading state - pulsing avatar skeleton
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  overflow: "hidden",
                  ml: 0.5,
                }}
              >
                <Skeleton
                  variant="circular"
                  width={40}
                  height={40}
                  animation="pulse"
                  sx={{
                    backgroundColor: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)",
                    animation: `${pulse} 1.5s ease-in-out infinite`,
                  }}
                />
              </Box>
            ) : user ? (
              // User is authenticated
              <Box>
                <IconButton
                  ref={anchorRef}
                  onClick={handleMenuOpen}
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    border: menuOpen ? `2px solid ${accentPrimary}` : "2px solid transparent",
                    boxShadow: menuOpen ? `0 0 12px ${accentPrimary}40` : "none",
                    "&:hover": {
                      backgroundColor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
                    },
                    "&:focus-visible": {
                      outline: `2px solid ${accentPrimary}`,
                      outlineOffset: "2px",
                    },
                  }}
                >
                  {avatarUrl ? (
                    <Avatar
                      src={avatarUrl}
                      sx={{ width: 32, height: 32 }}
                    />
                  ) : (
                    <AccountCircleIcon sx={{ width: 32, height: 32, color: "text.secondary" }} />
                  )}
                </IconButton>
                <Menu
                  anchorEl={anchorRef.current}
                  open={menuOpen}
                  onClose={handleMenuClose}
                  TransitionProps={{
                    timeout: 200,
                  }}
                  sx={{
                    "& .MuiPaper-root": {
                      background: isDark
                        ? "linear-gradient(135deg, rgba(30, 30, 35, 0.98) 0%, rgba(20, 20, 25, 0.99) 100%)"
                        : "linear-gradient(135deg, rgba(255, 255, 255, 0.99) 0%, rgba(248, 250, 252, 0.98) 100%)",
                      color: theme.palette.text.primary,
                      border: `1.5px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)"}`,
                      borderRadius: "14px",
                      backdropFilter: "blur(12px)",
                      boxShadow: `0 8px 32px -8px rgba(0,0,0,${isDark ? "0.5" : "0.2"})`,
                      minWidth: "200px",
                      mt: 1,
                      overflow: "hidden",
                    },
                  }}
                >
                  {/* Email header */}
                  <MenuItem
                    disabled
                    sx={{ 
                      fontFamily: "'JetBrains Mono', 'IBM Plex Mono', monospace",
                      fontSize: "0.75rem",
                      letterSpacing: "-0.01em",
                      opacity: 0.6,
                      py: 1.25,
                    }}
                  >
                    {user.email}
                  </MenuItem>

                  {/* Menu items with icons */}
                  {menuItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.path || 
                      location.pathname.startsWith(item.path + "/");
                    
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        style={{ textDecoration: "none", color: "inherit" }}
                      >
                        <MenuItem
                          onClick={handleMenuClose}
                          sx={{ 
                            fontFamily: "'JetBrains Mono', 'IBM Plex Mono', monospace",
                            fontSize: "0.85rem",
                            py: 1.25,
                            px: 2,
                            gap: 1.5,
                            transition: "all 0.2s ease",
                            // Active state
                            backgroundColor: isActive 
                              ? `${accentPrimary}12` 
                              : "transparent",
                            borderLeft: isActive 
                              ? `2px solid ${accentPrimary}` 
                              : "2px solid transparent",
                            color: isActive ? accentPrimary : "inherit",
                            "&:hover": {
                              background: `linear-gradient(90deg, ${accentPrimary}15, transparent)`,
                              color: accentPrimary,
                            },
                            "&:focus-visible": {
                              outline: `2px solid ${accentPrimary}`,
                              outlineOffset: "-2px",
                            },
                          }}
                        >
                          <Icon sx={{ fontSize: 18, opacity: 0.8 }} />
                          {item.label}
                        </MenuItem>
                      </Link>
                    );
                  })}

                  {/* Sign out */}
                  <MenuItem
                    onClick={() => {
                      handleSignOut();
                      handleMenuClose();
                    }}
                    sx={{ 
                      fontFamily: "'JetBrains Mono', 'IBM Plex Mono', monospace",
                      fontSize: "0.85rem",
                      py: 1.25,
                      px: 2,
                      gap: 1.5,
                      borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`,
                      mt: 0.5,
                      transition: "all 0.2s ease",
                      "&:hover": {
                        background: `linear-gradient(90deg, ${accentSecondary}15, transparent)`,
                        color: accentSecondary,
                      },
                      "&:focus-visible": {
                        outline: `2px solid ${accentSecondary}`,
                        outlineOffset: "-2px",
                      },
                    }}
                  >
                    <LogoutIcon sx={{ fontSize: 18, opacity: 0.8 }} />
                    sign out
                  </MenuItem>
                </Menu>
              </Box>
            ) : (
              // User is not authenticated
              <Link
                to={`/login?redirectTo=${encodeURIComponent(
                  location.pathname + location.search + location.hash
                )}`}
                style={{ textDecoration: "none" }}
              >
                <Button 
                  variant="contained"
                  sx={{
                    fontFamily: "'JetBrains Mono', 'IBM Plex Mono', monospace",
                    fontSize: isMobile ? "0.75rem" : "0.8rem",
                    fontWeight: 600,
                    letterSpacing: "-0.01em",
                    borderRadius: "10px",
                    px: isMobile ? 2 : 2.5,
                    py: 0.75,
                    backgroundColor: accentPrimary,
                    color: isDark ? "#121212" : "#fff",
                    boxShadow: `0 4px 14px -2px ${accentPrimary}40`,
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      backgroundColor: accentPrimary,
                      transform: "translateY(-1px)",
                      boxShadow: `0 6px 20px -2px ${accentPrimary}50`,
                    },
                    "&:focus-visible": {
                      outline: `2px solid ${accentPrimary}`,
                      outlineOffset: "2px",
                    },
                  }}
                >
                  sign in
                </Button>
              </Link>
            )}
          </Box>
        </Toolbar>
      </Box>
    </AppBar>
  );
}

export default Header;
